import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef, type OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';

export type BesharpSidebarItem = {
  category?: boolean;
  divider?: boolean;
  spacer?: boolean;
  icon?: string;
  command?: () => void;
  link?: string;
  label?: string;
  route?: string;
  useTemplate?: boolean;
  [key: string]: any;
};

export type BesharpSidebarConfig = {
  header?: string;
  subHeader?: string;
  foldable?: boolean;
  class?: string;
  loading?: boolean;
  items: BesharpSidebarItem[];
};

@Component({
  selector: 'app-besharp-sidebar',
  imports: [CommonModule],
  templateUrl: './besharp-sidebar.component.html',
  styleUrl: './besharp-sidebar.component.scss',
})
export class BesharpSidebarComponent implements OnInit {
  @Input()
  sidebarConfiguration!: BesharpSidebarConfig;
  @Output()
  sidebarSizeChanged = new EventEmitter<string>();
  private _sidebarSize: string = '300px';
  protected collapsed: boolean = false;
  protected sidebarWidth: string = this._sidebarSize;

  @ContentChild('sidebarFooterTemplate') sidebarFooterTemplate?: TemplateRef<any>;
  @ContentChild('headerTemplate') headerTemplate?: TemplateRef<any>;
  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<{ $implicit: BesharpSidebarItem }>;
  @ContentChild('loaderTemplate') loaderTemplate?: TemplateRef<any>;

  private router = inject(Router);

  constructor() {}

  protected get isLoading(): boolean {
    return this.sidebarConfiguration.loading ?? false;
  }

  ngOnInit(): void {
    if (!this.sidebarConfiguration) {
      throw 'You need to add a sidebar configuration to use this component!';
    }
    this.sidebarSizeChanged.emit(this.sidebarWidth);
  }

  toggleSidebar() {
    if (this.sidebarConfiguration.foldable) {
      this.sidebarWidth = this.sidebarWidth === this._sidebarSize ? '0' : this._sidebarSize;
      this.sidebarSizeChanged.emit(this.sidebarWidth);
      if (this.collapsed) {
        setTimeout(() => {
          this.collapsed = false;
        }, 280);
      } else {
        this.collapsed = true;
      }
    }
  }

  protected isExternalLink(link?: string): boolean {
    return !!link && /^https?:\/\//i.test(link);
  }

  protected onItemLinkClick(event: MouseEvent, item: BesharpSidebarItem): void {
    if (!item.link) return;
    if (this.isExternalLink(item.link)) return;

    event.preventDefault();
    this.router.navigateByUrl(item.link);
  }

  handleMiddleClick(event: MouseEvent, item: BesharpSidebarItem): void {
    if (event.button === 1) {
      event.preventDefault();
      event.stopPropagation();
      if (item.link) {
        const url = this.isExternalLink(item.link)
          ? item.link
          : `${window.location.origin}${item.link.startsWith('/') ? item.link : `/${item.link}`}`;
        window.open(url, '_blank');
      } else if (item.command) {
        this.openCommandInNewTab(item);
      }
    }
  }

  private openCommandInNewTab(item: BesharpSidebarItem): void {
    if (item.route) {
      const url = window.location.origin + item.route;
      window.open(url, '_blank');
      return;
    }
    const routeMap: { [key: string]: string } = {
      Chat: '/app/chat',
    };
    const route = routeMap[item.label || ''];
    if (route) {
      const url = window.location.origin + route;
      window.open(url, '_blank');
    }
  }
}
